import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    ScatterChart, Scatter, Cell, ComposedChart
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';
import { calculateColorGradientValue } from '../../utils/colorUtils';
import dayjs from 'dayjs';

interface ScatterChartContentProps {
    result: any,
    xAxisDataKey: string,
    yAxisDataKey: string,
    chartName: string,
    dataValueForLowColor: number,
    lowColor: string,
    dataValueForHighColor: number,
    highColor: string,
    defaultColor: string,
    height: number,
    width: number
}

export default function ScatterChartContent({
    result,
    xAxisDataKey,
    yAxisDataKey,
    chartName,
    dataValueForLowColor,
    lowColor,
    dataValueForHighColor,
    highColor,
    defaultColor,
    height,
    width
}: ScatterChartContentProps) {
    const theme = useTheme();
    var timeseriesData: any[] = [];

    const dateFormatter = (date: any) => {
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) {
            console.error("Invalid date value:", date);
            return "Invalid Date";
        }

        // Show label only for August of each year
        if (parsedDate.getMonth() === 7) {
            return dayjs(date).format('MMM YYYY'); // Aug YYYY
        }

        // Return an empty string for other months
        return '';
    };

    try {
        // Convert result to dict for Recharts
        _.zipWith(result.data, result.time_steps, function (value: number, timestep: string) {
            const formattedTimestep = dayjs(timestep).format('MMM YYYY'); // Format the date to "Month Year"

            timeseriesData.push({
                [xAxisDataKey]: formattedTimestep,
                [yAxisDataKey]: Math.round(value * 100) / 100,
                color: calculateColorGradientValue(lowColor, highColor, dataValueForLowColor, dataValueForHighColor, value, defaultColor)
            });
        });
    } catch (e: any) {
        console.log(e);
    }

    function linearRegression(points: { x: number, y: number }[]) {
        const N = points.length;
        const sumX = points.reduce((acc, p) => acc + p.x, 0);
        const sumY = points.reduce((acc, p) => acc + p.y, 0);
        const sumXY = points.reduce((acc, p) => acc + p.x * p.y, 0);
        const sumXX = points.reduce((acc, p) => acc + p.x * p.x, 0);

        const denominator = (N * sumXX - sumX * sumX);
        if (denominator === 0) return null;

        const m = (N * sumXY - sumX * sumY) / denominator;
        const b = (sumY - m * sumX) / N;
        return { m, b };
    }

    function convertData(data: any) {
        const transformed = data.map((d: any) => {
            const parsedDate = dayjs(d[xAxisDataKey], "MMM YYYY").startOf('month');
            return {
                date: parsedDate.valueOf(),
                value: d[yAxisDataKey],
                color: d.color
            };
        });

        // Compute regression
        const points = transformed.map((d: any) => ({ x: d.date, y: d.value }));
        const lr = linearRegression(points);
        if (lr) {
            const { m, b } = lr;
            const xs = transformed.map((d: any) => d.date);
            const xmin = Math.min(...xs);
            const xmax = Math.max(...xs);

            const ymin = m * xmin + b;
            const ymax = m * xmax + b;

            // Add two computed trend objects at the end
            transformed.push(
                { date: xmin, trend: ymin, color: "" },
                { date: xmax, trend: ymax, color: "" }
            );
        }

        return transformed;
    }

    const finalData = convertData(timeseriesData);

    function getTicks(month: number) {
        const ticks: any[] = [];
        const years = new Set<number>();

        finalData.forEach(function (item: any) {
            const date = new Date(item.date);
            const year = date.getFullYear();
            if (date.getMonth() === month && !years.has(year)) {
                ticks.push(item.date);
                years.add(year);
            }
        });

        return ticks;
    }

    const ticks = getTicks(7); // August ticks only
    console.log("final Data", finalData);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
                data={finalData}
                margin={{ top: 5, right: 30, bottom: 5 }}
            >
                <CartesianGrid stroke={theme.palette.text.secondary} />
                <XAxis
                    tickFormatter={dateFormatter}
                    ticks={ticks}
                    dataKey="date"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    interval={0}
                />
                <YAxis
                    type="number"
                    domain={[0, 1]}
                    ticks={[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]}
                    orientation="right"
                />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Legend />
                <Scatter dataKey="value" fill={defaultColor} name={chartName}>
                    {finalData.map((entry: any, index: any) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                </Scatter>
                <Line
                    dot={false}
                    strokeDasharray="4"
                    dataKey="trend"
                    stroke="white"
                    isAnimationActive={false}
                    legendType="none"
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
}